import React, { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Bot } from "lucide-react";
import { Link } from "react-router-dom";

const SUGGESTIONS = [
  "What is your MOQ?",
  "What are your lead times?",
  "Do you ship to my country?",
  "Can I send my own artwork?",
  "Do you provide samples?",
  "What categories do you offer?",
];

const KB = [
  { k: ["moq", "minimum"],                                                         a: "Our MOQs vary by design path: ~300–500 units for library designs, 500–1000 for Indus Art exclusives, and 1000+ for fully bespoke client-supplied artwork. Share your brief and we'll confirm exactly for your category." },
  { k: ["lead time", "delivery", "how long", "production"],                         a: "Production runs 30–90 days depending on volume and design path. Sampling adds 2–4 weeks for exclusive or custom artwork. We confirm firm dates in writing with every quote." },
  { k: ["ship", "country", "export", "deliver"],                                    a: "Yes — we export to 30+ countries across North America, EU, UK, Japan, Australia, the Middle East and Latin America. We handle full export documentation and can meet buyer-specified compliance standards." },
  { k: ["artwork", "design", "own design", "custom", "bespoke"],                    a: "Absolutely. Our Custom Orders path accepts CAD, illustrations, or hand-drawn artwork. We translate it into hand-carved teak blocks and sample before bulk production. Design/setup fees apply." },
  { k: ["sample", "swatch"],                                                        a: "Swatches are available on every confirmed inquiry. Finished-unit samples are available above a minimum MOQ threshold — couriered globally, tracked." },
  { k: ["category", "products", "range", "catalog", "catalogue", "offer"],          a: "We offer 14 wholesale categories: Bedding, Quilts & Dohars, Table Linen, Curtains, Cushions, Fabric by the Yard, Kids, Kitchen Linen, Throws, Bath, Table Runners, Napery, Scarves & Stoles, and fully Custom Designs." },
  { k: ["price", "pricing", "cost", "quote", "rate"],                               a: "Every quote is built privately for your MOQ, market, and design path. Share a rough brief via the quote form and we'll come back within one business day with pricing, lead time, and a recommended path." },
  { k: ["certif", "compliance", "gots", "oeko", "sa8000", "sedex", "bsci"],         a: "We work towards GOTS, OEKO-TEX, SA8000, Sedex, and BSCI standards. Specific certification scopes per facility can be shared on request with NDAs where required." },
  { k: ["return", "defect", "reject", "qc"],                                        a: "Our 100% piece-by-piece inspection by our women-led QC team minimises defects. Any issue found on receipt is investigated within 5 business days and resolved through rework, replacement, or credit per the agreed PO terms." },
  { k: ["private label", "white label"],                                            a: "Yes — we offer private label and white label services: custom labelling, packaging, hang-tags, and barcoding per your SKU system. We also support retail-ready presentation formats." },
  { k: ["contact", "email", "phone", "whatsapp", "call"],                           a: "Email us at export@indusart.in · Phone +91 80489 56816 (WhatsApp available on the same number). We respond within 24 hours." },
  { k: ["address", "where", "located", "location", "jaipur"],                       a: "Our workshop is at H-3, RIICO Industrial Area, Mansarovar, Jaipur, Rajasthan 302029, India. Buyer visits welcome with prior appointment." },
  { k: ["partner", "relationship manager", "long term"],                            a: "Accounts above a qualifying order volume or a repeat relationship qualify for a dedicated relationship manager, priority sampling, and stocked-fabric programs. Start with a quote — we take it from there." },
  { k: ["hello", "hi", "hey", "good morning", "good afternoon", "good evening"],    a: "Hi 👋 I'm the Indus Art assistant. Ask me about MOQs, lead times, custom designs, samples, shipping, or anything else — or pick a suggestion below." },
  { k: ["thank"],                                                                   a: "You're welcome. Ready for a personalised quote? Tap 'Request a Quote' anytime." },
];

function answer(q) {
  const low = q.toLowerCase();
  for (const item of KB) {
    if (item.k.some((kw) => low.includes(kw))) return item.a;
  }
  return "I'll route this to a specialist — best to fill the quick quote form so we can come back in one business day with a complete answer. Would you like me to open it?";
}

export default function ChatBot({ open, onClose }) {
  const [messages, setMessages] = useState([
    { from: "bot", text: "Hi 👋 I'm the Indus Art assistant. How can I help you today?" },
  ]);
  const [input, setInput] = useState("");
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const send = (text) => {
    if (!text.trim()) return;
    const user = { from: "user", text: text.trim() };
    setMessages((m) => [...m, user]);
    setInput("");
    setTimeout(() => {
      setMessages((m) => [...m, { from: "bot", text: answer(text) }]);
    }, 450);
  };

  if (!open) return null;

  return (
    <div
      data-testid="chatbot-widget"
      className="fixed bottom-24 right-5 z-50 w-[380px] max-w-[calc(100vw-2rem)] h-[560px] max-h-[calc(100vh-8rem)] rounded-[1.75rem] bg-white shadow-soft flex flex-col overflow-hidden border border-[#1a1a1a]/10 animate-in fade-in slide-in-from-bottom-4 duration-300"
    >
      {/* header */}
      <div className="bg-indigo-gradient text-[#faf6ef] px-5 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur flex items-center justify-center">
            <Bot size={18} strokeWidth={2} />
          </div>
          <div>
            <div className="font-bold text-sm">Indus Art Assistant</div>
            <div className="text-[11px] text-[#faf6ef]/70 flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-[#8fbf7a] animate-pulse" />
              Online · replies in seconds
            </div>
          </div>
        </div>
        <button onClick={onClose} data-testid="chatbot-close" aria-label="Close chat" className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition">
          <X size={16} strokeWidth={2} />
        </button>
      </div>

      {/* messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 bg-cream-gradient space-y-3">
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.from === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[80%] px-4 py-2.5 text-sm leading-[1.55] rounded-2xl ${
              m.from === "user"
                ? "bg-[#1e2a5a] text-[#faf6ef] rounded-br-md"
                : "bg-white text-[#1a1a1a] shadow-sm rounded-bl-md"
            }`}>
              {m.text}
            </div>
          </div>
        ))}
      </div>

      {/* quick suggestions */}
      {messages.length <= 2 && (
        <div className="px-4 py-3 flex flex-wrap gap-2 bg-white border-t border-[#1a1a1a]/5">
          {SUGGESTIONS.slice(0, 4).map((s) => (
            <button key={s} data-testid="chatbot-suggestion" onClick={() => send(s)}
              className="text-[11px] font-semibold text-[#1e2a5a] bg-[#1e2a5a]/8 hover:bg-[#1e2a5a]/15 px-3 py-1.5 rounded-full transition">
              {s}
            </button>
          ))}
        </div>
      )}

      {/* input */}
      <form
        onSubmit={(e) => { e.preventDefault(); send(input); }}
        className="p-3 bg-white border-t border-[#1a1a1a]/10 flex items-center gap-2"
      >
        <input
          data-testid="chatbot-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Type your question…"
          className="flex-1 bg-[#faf6ef] rounded-full px-4 py-2.5 text-sm placeholder:text-[#1a1a1a]/40 focus:outline-none focus:ring-2 focus:ring-[#1e2a5a]/20"
        />
        <button type="submit" data-testid="chatbot-send" aria-label="Send" disabled={!input.trim()}
          className="w-10 h-10 rounded-full bg-[#1e2a5a] text-[#faf6ef] hover:bg-[#b4462a] flex items-center justify-center transition disabled:opacity-40">
          <Send size={16} strokeWidth={2} />
        </button>
      </form>

      {/* footer CTA */}
      <div className="px-4 py-3 bg-[#faf6ef] border-t border-[#1a1a1a]/5 flex items-center justify-between gap-2">
        <span className="text-[11px] text-[#1a1a1a]/55">Need a detailed quote?</span>
        <Link to="/contact#lead-form" onClick={onClose} data-testid="chatbot-to-quote"
          className="text-[11px] font-bold text-[#b4462a] hover:text-[#1e2a5a] transition">
          Request a Quote →
        </Link>
      </div>
    </div>
  );
}
